import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

/* Layout Components */
import Topbar from "./components/Topbar";
import Header from "./components/Header";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

/* Pages */
import Home from "./pages/Home";
import About from "./pages/About";
import Poojas from "./pages/Poojas";
import PoojaDetails from "./pages/PoojaDetails";
import Timings from "./pages/Timings";
import Festivals from "./pages/Festivals";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import Account from "./pages/Account";

/* 🌟 Extra Pages */
import SpiritualLinks from "./pages/SpiritualLinks";
import LiveDarshan from "./pages/LiveDarshan";
import Donate from "./pages/Donate";
import Profile from "./pages/Profile";
import Idols from "./pages/Idols";
import ShortsGallery from "./pages/ShortsGallery";
import NakshatraHomam from "./pages/NakshatraHomam";
import HomamRegistrationPage from "./pages/HomamRegistrationPage";
import HomamRegistrationSplitPage from "./pages/HomamRegistrationSplitPage";
import HomamRegistrationDirectPage from "./pages/HomamRegistrationDirectPage";
import HomamBookingPage from "./pages/homam/HomamBookingPage";
import HomamToday from "./pages/homam/HomamToday";
import HomamDetail from "./pages/homam/HomamDetail";
import HomamGallery from "./pages/homam/HomamGallery";
import HomamContact from "./pages/homam/HomamContact";
import HomamAdPopup from "./components/HomamAdPopup";
import MandirPopupModal from "./components/MandirPopupModal";
import "./utils/customPopup";
import { useHomam } from "./context/HomamContext";
import { isHomamSubdomain } from "./utils/domain";

/* 👑 Admin Components & Pages */
import "./admin/App.css";
import Sidebar from "./admin/components/Sidebar";
import ProtectedRoute from "./admin/components/ProtectedRoute";
import Login from "./admin/pages/Login";
import ManageFestivals from "./admin/pages/ManageFestivals";
import ManageGallery from "./admin/pages/ManageGallery";
import ManagePoojas from "./admin/pages/ManagePoojas";
import ManageTimings from "./admin/pages/ManageTimings";
import ManageAbout from "./admin/pages/ManageAbout";
import ManageHome from "./admin/pages/ManageHome";
import ManageIdols from "./admin/pages/ManageIdols";
import ManageLive from "./admin/pages/ManageLive";
import ManageLinks from "./admin/pages/ManageLinks";
import ManageContact from "./admin/pages/ManageContact";
import ManageUsers from "./admin/pages/ManageUsers";
import ManageDonation from "./admin/pages/ManageDonation";
import ManageHomamBookings from "./admin/pages/ManageHomamBookings";
import ManagePoojaBookings from "./admin/pages/ManagePoojaBookings";
import ManageAccount from "./admin/pages/ManageAccount";
import Dashboard from "./admin/pages/Dashboard";

/* 404 Page */
const NotFound = () => (
  <div style={{ textAlign: "center", padding: "100px 20px" }}>
    <h1 style={{ fontSize: "60px", margin: 0 }}>404</h1>
    <p style={{ fontSize: "18px", color: "#666" }}>Page Not Found</p>
  </div>
);

/* Scroll To Top Handler */
const ScrollToTop = ({ children }) => {
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  return children;
};

/* Layout Wrapper */
function AppLayout() {
  const location = useLocation();
  const { isHomamActive } = useHomam();
  const isSubdomain = isHomamSubdomain();

  const isAuthPage = location.pathname === "/account";
  const isHomamRoute =
    (Boolean(isHomamActive) &&
      (location.pathname.startsWith("/nakshatra-homam") ||
        location.pathname.startsWith("/homam-booking") ||
        (location.pathname.startsWith("/homam-registration") && location.pathname !== "/homam-registration") ||
        location.pathname === "/booking")) ||
    (Boolean(isHomamActive) && isSubdomain);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isAdminLogin = location.pathname === "/admin/login";

  // ================= ADMIN PORTAL =================
  if (isAdminRoute) {
    if (isAdminLogin) {
      return (
        <Routes>
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/*" element={<Navigate to="/admin/login" replace />} />
        </Routes>
      );
    }

    return (
      <div className="admin-layout">
        <Sidebar />
        <main className="main-content">
          <div className="content-wrapper">
            <Routes>
              {/* PROTECTED ROUTES */}
              <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/admin/home" element={<ProtectedRoute><ManageHome /></ProtectedRoute>} />
              <Route path="/admin/about" element={<ProtectedRoute><ManageAbout /></ProtectedRoute>} />
              <Route path="/admin/festivals" element={<ProtectedRoute><ManageFestivals /></ProtectedRoute>} />
              <Route path="/admin/gallery" element={<ProtectedRoute><ManageGallery /></ProtectedRoute>} />
              <Route path="/admin/poojas" element={<ProtectedRoute><ManagePoojas /></ProtectedRoute>} />
              <Route path="/admin/timings" element={<ProtectedRoute><ManageTimings /></ProtectedRoute>} />
              <Route path="/admin/idols" element={<ProtectedRoute><ManageIdols /></ProtectedRoute>} />
              <Route path="/admin/live-darshan" element={<ProtectedRoute><ManageLive /></ProtectedRoute>} />
              <Route path="/admin/links" element={<ProtectedRoute><ManageLinks /></ProtectedRoute>} />
              <Route path="/admin/contact" element={<ProtectedRoute><ManageContact /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute><ManageUsers /></ProtectedRoute>} />
              <Route path="/admin/donations" element={<ProtectedRoute><ManageDonation /></ProtectedRoute>} />
              <Route path="/admin/pooja-bookings" element={<ProtectedRoute><ManagePoojaBookings /></ProtectedRoute>} />
              <Route path="/admin/homam-bookings" element={<ProtectedRoute><ManageHomamBookings /></ProtectedRoute>} />
              <Route path="/admin/account" element={<ProtectedRoute><ManageAccount /></ProtectedRoute>} />
              
              {/* DEFAULT REDIRECTS */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    );
  }

  // ================= 27 NAKSHATRA HOMAM SUBDOMAIN PORTAL =================
  // (Triggered when accessed via 27nakshathrahomam.srignanasaibabamandir.com)
  if (isSubdomain) {
    return (
      <ScrollToTop>
        <div className="app homam-subdomain-app">
          <main className="homam-main">
            <Routes>
              {/* Primary Subdomain Routes (Clean URLs) */}
              <Route path="/" element={<HomamRegistrationDirectPage />} />
              <Route path="/today" element={<HomamToday />} />
              <Route path="/nakshatra/:slug" element={<HomamDetail />} />
              <Route path="/booking" element={<HomamBookingPage />} />
              <Route path="/homam-booking" element={<HomamBookingPage />} />
              <Route path="/registration" element={<HomamRegistrationPage />} />
              <Route path="/homam-registration" element={<HomamRegistrationPage />} />
              <Route path="/registration-split" element={<HomamRegistrationSplitPage />} />
              <Route path="/homam-registration-split" element={<HomamRegistrationSplitPage />} />
              <Route path="/registration-direct" element={<NakshatraHomam />} />
              <Route path="/homam-registration-direct" element={<NakshatraHomam />} />
              <Route path="/gallery" element={<HomamGallery />} />
              <Route path="/contact" element={<HomamContact />} />

              {/* Backward-compatibility / nested paths */}
              <Route path="/nakshatra-homam" element={<HomamRegistrationDirectPage />} />
              <Route path="/nakshatra-homam/today" element={<Navigate to="/today" replace />} />
              <Route path="/nakshatra-homam/nakshatra/:slug" element={<HomamDetail />} />
              <Route path="/nakshatra-homam/booking" element={<HomamBookingPage />} />
              <Route path="/nakshatra-homam/registration-split" element={<HomamRegistrationSplitPage />} />
              <Route path="/nakshatra-homam/registration-direct" element={<NakshatraHomam />} />
              <Route path="/nakshatra-homam/gallery" element={<Navigate to="/gallery" replace />} />
              <Route path="/nakshatra-homam/contact" element={<Navigate to="/contact" replace />} />

              {/* Fallback to Homam Home on Subdomain */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </ScrollToTop>
    );
  }

  // ================= USER PORTAL (MAIN TEMPLE SITE) =================
  return (
    <ScrollToTop>
      <div className="app">

        {/* HEADER SECTION (hidden on auth page and dedicated homam portal) */}
        {!isAuthPage && !isHomamRoute && <Topbar />}
        {!isAuthPage && !isHomamRoute && <Header />}
        {!isAuthPage && !isHomamRoute && <Navbar />}

        {/* MAIN CONTENT */}
        <main className={isAuthPage ? "auth-main" : isHomamRoute ? "homam-main" : "main-content"}>

          <Routes>

            {/* ================= MAIN ROUTES ================= */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/poojas" element={<Poojas />} />
            <Route path="/poojas/:id" element={<PoojaDetails />} />
            <Route path="/timings" element={<Timings />} />
            <Route path="/festivals" element={<Festivals />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/contact" element={<Contact />} />

            {/* ================= 27 NAKSHATRA HOMAM (100% REACT) ================= */}
            {isHomamActive ? (
              <>
                <Route path="/nakshatra-homam" element={<HomamRegistrationDirectPage />} />
                <Route path="/nakshatra-homam/today" element={<HomamToday />} />
                <Route path="/nakshatra-homam/nakshatra/:slug" element={<HomamDetail />} />
                <Route path="/nakshatra-homam/booking" element={<HomamBookingPage />} />
                <Route path="/nakshatra-homam/gallery" element={<HomamGallery />} />
                <Route path="/nakshatra-homam/contact" element={<HomamContact />} />
              </>
            ) : (
              <>
                <Route path="/nakshatra-homam" element={<Navigate to="/" replace />} />
                <Route path="/nakshatra-homam/*" element={<Navigate to="/" replace />} />
              </>
            )}

            {/* ================= HOMAM REGISTRATION & SEVA BOOKING PAGES ================= */}
            <Route path="/homam-registration" element={<HomamRegistrationPage />} />
            <Route path="/homam-registration-split" element={<HomamRegistrationSplitPage />} />
            <Route path="/homam-registration-direct" element={<NakshatraHomam />} />
            <Route path="/homam-booking" element={<HomamBookingPage />} />
            <Route path="/booking" element={<HomamBookingPage />} />
            <Route path="/nakshatra-homam-registration" element={<Navigate to="/homam-registration" replace />} />
            <Route path="/nakshatra-homam/registration" element={<Navigate to="/homam-registration" replace />} />
            <Route path="/nakshatra-homam/registration-split" element={<HomamRegistrationSplitPage />} />
            <Route path="/nakshatra-homam/registration-direct" element={<NakshatraHomam />} />

            {/* ================= EXTRA FEATURES ================= */}
            <Route path="/spiritual-links" element={<SpiritualLinks />} />
            <Route path="/live-darshan" element={<LiveDarshan />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/idols" element={<Idols />} />
            <Route path="/shorts" element={<ShortsGallery />} />

            {/* ================= AUTH ================= */}
            <Route path="/account" element={<Account />} />

            {/* ================= 404 ================= */}
            <Route path="*" element={<NotFound />} />

          </Routes>

        </main>

        {/* FOOTER SECTION (hidden on auth page and dedicated homam portal) */}
        {!isAuthPage && !isHomamRoute && <Footer />}

        {/* NAKSHATRA HOMAM ADVERTISEMENT (5s modal -> bottom-right floating banner on non-homam pages) */}
        {isHomamActive && <HomamAdPopup />}

      </div>
    </ScrollToTop>
  );
}

/* ROOT APP */
function App() {
  return (
    <Router>
      <AppLayout />
      <MandirPopupModal />
    </Router>
  );
}

export default App;